import { memo, createRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Checkbox from 'client/components/GDS/Checkbox/Checkbox';

import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';

import styles from './TreeItem.module.scss';

const TreeItem = ({ children, id, label, checked, indeterminate, disabled, onChange, open, onOpen, ...props }) => {
    const chevronRef = createRef();
    const intl = useIntl();
    const lowercaseLabel = label.toLowerCase();

    const [isOpen, setIsOpen] = useState(Boolean(open));

    useEffect(() => {
        setIsOpen(Boolean(open));
    }, [open]);

    const handleClick = () => {
        const open = !isOpen;
        onOpen({ id, open });
        setIsOpen(open);
        if (chevronRef?.current && !disabled) {
            chevronRef.current.focus({
                preventScroll: true
            });
        }
    };

    const handleOnChange = evt => {
        onChange({ ...evt, id, label });
    };

    return (
        <div className={styles.container} data-testid={props['data-testid']}>
            <div className={styles.header}>
                <Checkbox
                    checked={Boolean(checked)}
                    indeterminate={indeterminate}
                    onChange={handleOnChange}
                    label={label}
                    name={id}
                    disabled={disabled}
                    data-testid={props['data-testid'] + '.component' }
                />
                {(children !== null && typeof children !== 'undefined') &&
                    <div className={styles.toggle} >
                        <button
                            className={styles.chevron}
                            role="button"
                            aria-label={
                                (isOpen
                                    ? intl.formatMessage({ id: 'treeControls.collapse' })
                                    : intl.formatMessage({ id: 'treeControls.expand' })) +
                                    lowercaseLabel
                            }
                            onClick={handleClick}
                            tabIndex={0}
                            ref={chevronRef}
                            aria-expanded={isOpen}
                        >
                            <Icon
                                className={styles.icon}
                                path={isOpen ? mdiChevronUp : mdiChevronDown}
                                size={1.1}
                                role="presentation"
                            />
                        </button>

                    </div>
                }
            </div>
            { isOpen && <div className={[styles.content, !isOpen ? styles.closed : null].join(' ')} >{children}</div> }
        </div>
    );
};

TreeItem.propTypes = {
    id: PropTypes.string.isRequired,
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    open: PropTypes.bool,
    onOpen: PropTypes.func,
    children: PropTypes.any,
    disabled: PropTypes.bool,
    'data-testid': PropTypes.string
};

TreeItem.defaultProps = {
    checked: false,
    indeterminate: false,
    onChange: () => {},
    label: 'label',
    open: false,
    disabled: false
};

export default memo(TreeItem);
